import { extend, type RequestOptionsInit } from 'umi-request';
import { notification } from 'antd';
import { get, split } from 'lodash';
import { getEnv } from '@/config/env';
import { triggerTurnstileVerification } from '@/context/TurnstileContext';
import { reportGlobalNetworkError, reportGlobalNetworkSuccess, isGlobalNetworkUnstable } from '@/context/NetworkStatusContext';
import { buildHttpErrorReportContext, renderReportIssueBtn } from './apiErrorReport';
import { getApiErrorInfo, isMfaError } from './apiError';

// Get base URL from config module
// In development, use the CORS proxy (http://localhost:3001) which forwards to the real API
// In production, use the configured API URL
const getBaseUrl = () => {
  return getEnv('ADMIN_API_URL') || '';
};

// ============================================================================
// 429 Rate Limit Retry Configuration
// ============================================================================
const RETRY_CONFIG = {
  /** Maximum number of retry attempts for 429 responses */
  maxRetries: 3,
  /** Base delay in ms before first retry (doubles each attempt) */
  baseDelayMs: 1000,
  /** Maximum delay in ms between retries */
  maxDelayMs: 10000,
  /** Jitter factor (0-1) to randomize delay and avoid thundering herd */
  jitterFactor: 0.2
} as const;

/**
 * Sleep for a given duration
 */
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay with jitter
 * @param attempt - Current retry attempt (0-indexed)
 * @returns Delay in milliseconds
 */
const calculateBackoffDelay = (attempt: number): number => {
  const exponentialDelay = RETRY_CONFIG.baseDelayMs * 2 ** attempt;
  const cappedDelay = Math.min(exponentialDelay, RETRY_CONFIG.maxDelayMs);
  const jitter = cappedDelay * RETRY_CONFIG.jitterFactor * Math.random();
  return cappedDelay + jitter;
};

const ERROR_MESSAGES = {
  INVOICE: {
    INVALID_USER: { title: 'User is invalid', description: '' },
    INVALID_INVOICE: { title: 'Invoice is invalid', description: '' },
    INVALID_PDF_GENERATION: { title: 'Pdf not found', description: '' },
    FAILED_PDF_GENERATION: { title: 'Failed to generate client document', description: '' },
    INVALID_WALLET: { title: 'Wallet is invalid', description: '' }
  }
};

const errorHandler = async (error: any) => {
  // Detect aborted / network-level errors early, before umi-request's error
  // mutation (which adds .response/.type/.data to the raw TypeError) tricks the
  // response-based handling below into showing a spurious notification.
  const isAbortedOrNetworkError =
    error.type === 'aborted' ||
    error.name === 'AbortError' ||
    (error instanceof TypeError && /Failed to fetch|Load failed|NetworkError/i.test(error.message));

  if (isAbortedOrNetworkError) {
    reportGlobalNetworkError();
    throw error;
  }

  // Handle different error structures:
  // 1. Standard umi-request error: error.response.data
  // 2. getResponse: true error: error.data (when response.data is empty)
  // 3. Network error: no response property
  let response = error.response;
  let data = get(response, 'data', {});

  // If response is not found, check if error itself has response-like properties
  // This can happen with getResponse: true when the error structure differs
  if (!response && error.status) {
    // Error object itself might be the response-like object
    response = error;
    data = error.data || {};
  } else if (!response && error.data) {
    // Some error structures have data directly on error
    data = error.data;
  }

  // Critical fix: When using getResponse: true, if response.data is empty but error.data exists,
  // use error.data as the actual error payload (this is the common case)
  if (response && Object.keys(data).length === 0 && error.data && typeof error.data === 'object' && Object.keys(error.data).length > 0) {
    data = error.data;
  }

  if (response && response.status) {
    const { status } = response;
    const key = `open-${Date.now()}`;
    const errorInfo = getApiErrorInfo(error);

    const rawMessage = errorInfo.message || (Array.isArray(data.message) ? data.message.join('; ') : data.message) || 'Unknown error';
    const reportContext = buildHttpErrorReportContext({
      status,
      message: rawMessage,
      errorCode: errorInfo.code || data.code,
      requestUrl: response.url,
      requestMethod: error.request?.options?.method,
      requestBody: error.request?.options?.data ?? error.request?.options?.body,
      responseBody: data
    });
    const reportBtn = renderReportIssueBtn(reportContext, key);

    // Quick Exchange quote (RFQ) errors are shown inline by the Quoter
    // (setQuoteError — same spot as "Insufficient amount of HKD"), so suppress
    // the duplicate global toast for that endpoint. The error still propagates
    // via `throw error` at the end of this handler, so the component still runs.
    // Note: matches only the quote endpoint, not `/exchanges/rfq/execute`.
    const isQuoteRfqRequest = /\/exchanges\/rfq(?:$|\?)/.test(String(response.url ?? ''));

    if (isQuoteRfqRequest) {
      // handled inline by the Quoter — no global toast
    } else if (window.location.pathname === '/account/settings') {
      notification.error({
        key,
        message: 'Update failed',
        description: data.message,
        duration: 10,
        btn: reportBtn
      });
    } else if (isMfaError(error)) {
      notification.error({
        key,
        message: 'MFA verification failed',
        description: 'The MFA token is invalid or expired. Enter a new token and submit again.',
        duration: 10,
        btn: reportBtn
      });
    } else if (status === 400) {
      const message = Array.isArray(data.message) ? data.message.join('; ') : data.message;
      const issueCardErrorTitles: Record<string, string> = {
        CARDHOLDER_NAME_MISMATCH: 'Cannot issue card',
        ISSUE_REJECTED: 'Cannot issue card'
      };
      const toastTitle =
        data.code === 'VALIDATION_ERROR'
          ? 'Validation Error'
          : (data.code && issueCardErrorTitles[data.code]) || data.code || 'Unexpected Error Occurred';

      notification.error({
        key,
        message: toastTitle,
        description: message || 'Please try again later.',
        duration: 10,
        btn: reportBtn
      });
    } else if (status === 406 && localStorage.getItem('token') && data?.message === 'Invalid Turnstile token') {
      // Clear invalid token and trigger re-verification modal
      localStorage.removeItem('turnstileToken');
      localStorage.removeItem('turnstileLastVerifiedAt');
      triggerTurnstileVerification().catch(() => {
        // Modal couldn't be shown (provider not mounted) - silent fail
      });
      notification.warning({
        key,
        message: 'Verification Required',
        description: 'Please complete the security verification and try again.'
      });
    } else if (status === 422 || status === 406) {
      const message = data?.message || 'Input validation failed';
      // skip notification for some message, component will handle the error, notification will be confuse the operator with double error message
      const skipNotificationMessages = [
        'FORMER_FEE_MODEL_FALLBACK',
        'Invalid Turnstile token',
        'ERR_NO_FEE_SCHEDULE',
        'ERR_INSUFFICIENT_BALANCE',
        'ERR_INVOICE_ISSUE_IN_PROGRESS',
        'ERR_INVOICE_NEGATIVE_TOTAL'
      ];
      // not early return, let the component handle the error
      if (!skipNotificationMessages.includes(message) && !skipNotificationMessages.includes(data?.code)) {
        notification.error({
          key,
          message: `Input Validation Failed`,
          description: split(message, '\n').map((line, idx) => (
            <span key={idx}>
              {line}
              <br />
            </span>
          )),
          duration: 10,
          btn: reportBtn
        });
      }
    } else if (status === 500) {
      if (data.name) {
        let message = ERROR_MESSAGES[data.name as keyof typeof ERROR_MESSAGES]
          ? ERROR_MESSAGES[data.name as keyof typeof ERROR_MESSAGES][
              data.code as keyof (typeof ERROR_MESSAGES)[keyof typeof ERROR_MESSAGES]
            ]
          : undefined;
        notification.error({
          key,
          message: message?.title ?? data.message,
          description: message?.description ?? '',
          duration: 10,
          btn: reportBtn
        });
      } else if (data.message === 'Insufficient balance to execute transaction') {
        notification.error({
          key,
          message: 'Insufficient Balance',
          description: 'Insufficient balance to execute transaction',
          duration: 10,
          btn: reportBtn
        });
      } else {
        const serverMessage =
          typeof data.message === 'string' && data.message.trim().length > 0 ? data.message.trim() : 'Please try again later.';
        notification.error({
          key,
          message: `Unexpected Error Occurred`,
          description: serverMessage,
          duration: 10,
          btn: reportBtn
        });
      }
    } else if (status === 409 && data.code === 'USER_CONTACT_CASCADE_REQUIRES_CONFIRMATION') {
      // Caller handles this with an in-flow confirmation modal — suppress the
      // generic toast so the operator only sees the modal.
    } else if (status === 409 && data.code === 'GI_CONCURRENT_MODIFICATION') {
      // General Instruction edit form shows its own modal + Refresh — suppress duplicate toast.
    } else if (status === 409 && data.code === 'ERR_INVOICE_ISSUE_IN_PROGRESS') {
      // Invoice form shows the bounded Temporal wait state inline.
    } else if (status === 502 || status === 503 || status === 504) {
      reportGlobalNetworkError();
      // Suppress toast when the connection banner is already visible
      if (!isGlobalNetworkUnstable()) {
        notification.warning({
          key: 'connection-unstable',
          message: 'Connection unstable',
          description: 'Temporary connection issue. Retrying automatically...',
          duration: 10
        });
      }
    } else if (status === 409) {
      // Conflict (e.g. concurrent action) — operator-facing; no Report issue (not treated as an unknown bug).
      const errorMessage = Array.isArray(data.message) ? data.message.join('; ') : data.message;
      notification.error({
        key,
        message: 'Request could not be completed',
        description: errorMessage || 'This action conflicts with the current state. Please try again.',
        duration: 10
      });
    } else {
      // Fallback for unhandled status codes (403, 404, 502, 504, etc.)
      const errorMessage = Array.isArray(data.message) ? data.message.join('; ') : data.message;
      notification.error({
        key,
        message: `Request Failed (${status})`,
        description: errorMessage || 'An unexpected error occurred. Please try again.',
        duration: 10,
        btn: reportBtn
      });
    }
  }

  if (!response) {
    // Network error — surface via the global "Connection unstable" banner
    // (NetworkStatusContext) instead of a toast. Transient blips were spamming
    // operators with "Failed to fetch" notifications.
    reportGlobalNetworkError();
  }
  throw error;
};

const baseRequest = extend({
  // Don't set prefix here - it's evaluated at module load time before config is ready
  // The prefix is added dynamically in the request interceptor below
  timeout: 30000,
  errorHandler: errorHandler
});

// Add response interceptor
// On 401, clear stored auth data. The SessionExpiryWarning component handles
// the user-facing logout flow (warning modal with "Stay Logged In" option).
// We only hard-redirect here if the token was already cleared (no renewal possible).
baseRequest.interceptors.response.use((response) => {
  if (response.ok) {
    reportGlobalNetworkSuccess();
  }
  if (response.status === 401) {
    const hadToken = !!localStorage.getItem('token');
    localStorage.removeItem('token');
    localStorage.removeItem('dashboard-latest-data');
    localStorage.removeItem('dashboard-last-fetch');
    if (!hadToken) {
      window.location.href = `/`;
    }
  }
  return response;
});

// Add request interceptor
baseRequest.interceptors.request.use((url, options) => {
  let headers: Record<string, string> = {};
  const token = localStorage.getItem('token');
  if (token) {
    headers = {
      ...headers,
      Authorization: `Bearer ${token}`,
      'turnstile-token': localStorage.getItem('turnstileToken') ?? ''
    };
  } else {
    headers = {
      ...headers,
      'turnstile-token': localStorage.getItem('turnstileToken') ?? ''
    };
  }

  return {
    url: url.startsWith('http') ? url : `${getBaseUrl()}${url}`,
    options: {
      ...options,
      headers: {
        ...options.headers,
        ...headers
      }
    }
  };
});

/**
 * Extended request options with retry control
 */
type RequestOptionsWithRetry = RequestOptionsInit & {
  /** Disable 429 retry for this request */
  skipRetry?: boolean;
};

// Silent request instance - same as baseRequest but without error notifications
// Used for background searches, prefetching, and operations where errors should be handled silently
const silentBaseRequest = extend({
  timeout: 30000
  // No errorHandler - errors pass through without notifications
});

// Add response interceptor (same as baseRequest)
silentBaseRequest.interceptors.response.use((response) => {
  if (response.ok) {
    reportGlobalNetworkSuccess();
  }
  if (response.status === 401) {
    const hadToken = !!localStorage.getItem('token');
    localStorage.removeItem('token');
    localStorage.removeItem('dashboard-latest-data');
    localStorage.removeItem('dashboard-last-fetch');
    if (!hadToken) {
      window.location.href = `/`;
    }
  }
  return response;
});

// Add request interceptor (same as baseRequest)
silentBaseRequest.interceptors.request.use((url, options) => {
  let headers: Record<string, string> = {};
  const token = localStorage.getItem('token');
  if (token) {
    headers = {
      ...headers,
      Authorization: `Bearer ${token}`,
      'turnstile-token': localStorage.getItem('turnstileToken') ?? ''
    };
  } else {
    headers = {
      ...headers,
      'turnstile-token': localStorage.getItem('turnstileToken') ?? ''
    };
  }

  return {
    url: url.startsWith('http') ? url : `${getBaseUrl()}${url}`,
    options: {
      ...options,
      headers: {
        ...options.headers,
        ...headers
      }
    }
  };
});

/**
 * Request wrapper with automatic 429 retry and exponential backoff.
 * Retries up to RETRY_CONFIG.maxRetries times when receiving a 429 response.
 *
 * @param url - Request URL
 * @param options - Request options (extends umi-request options with skipRetry)
 * @returns Promise resolving to the response data
 * @throws The original error after all retries are exhausted
 */
async function requestWithRetry<T = unknown>(url: string, options?: RequestOptionsWithRetry): Promise<T> {
  const { skipRetry = false, ...requestOptions } = options ?? {};

  let lastError: unknown;

  for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    try {
      return await baseRequest<T>(url, requestOptions);
    } catch (error: unknown) {
      lastError = error;

      // Check if this is a 429 error
      const response = (error as { response?: Response })?.response;
      const is429 = response?.status === 429;

      // Don't retry if:
      // - Not a 429 error
      // - Retry is explicitly disabled
      // - We've exhausted all retries
      if (!is429 || skipRetry || attempt >= RETRY_CONFIG.maxRetries) {
        throw error;
      }

      // Calculate backoff delay, respecting Retry-After header if present
      let delayMs = calculateBackoffDelay(attempt);
      const retryAfterHeader = response?.headers?.get('Retry-After');
      if (retryAfterHeader) {
        const retryAfterSeconds = Number.parseInt(retryAfterHeader, 10);
        if (!Number.isNaN(retryAfterSeconds)) {
          delayMs = Math.max(delayMs, retryAfterSeconds * 1000);
        }
      }

      console.warn(`[HTTP] Rate limited (429). Retrying in ${Math.round(delayMs)}ms (attempt ${attempt + 1}/${RETRY_CONFIG.maxRetries})`);

      await sleep(delayMs);
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError;
}

/**
 * HTTP request client with automatic 429 retry and exponential backoff.
 * Use this for all API requests to ensure rate limit handling.
 */
export const request = requestWithRetry;

/**
 * Silent request wrapper - same retry logic but no error notifications.
 * Use for background searches, prefetching, and operations where errors
 * should be handled silently by the calling code.
 */
async function silentRequestWithRetry<T = unknown>(url: string, options?: RequestOptionsWithRetry): Promise<T> {
  const { skipRetry = false, ...requestOptions } = options ?? {};

  let lastError: unknown;

  for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    try {
      return await silentBaseRequest<T>(url, requestOptions);
    } catch (error: unknown) {
      lastError = error;

      // Check if this is a 429 error
      const response = (error as { response?: Response })?.response;
      const is429 = response?.status === 429;

      // Don't retry if:
      // - Not a 429 error
      // - Retry is explicitly disabled
      // - We've exhausted all retries
      if (!is429 || skipRetry || attempt >= RETRY_CONFIG.maxRetries) {
        throw error;
      }

      // Calculate backoff delay, respecting Retry-After header if present
      let delayMs = calculateBackoffDelay(attempt);
      const retryAfterHeader = response?.headers?.get('Retry-After');
      if (retryAfterHeader) {
        const retryAfterSeconds = Number.parseInt(retryAfterHeader, 10);
        if (!Number.isNaN(retryAfterSeconds)) {
          delayMs = Math.max(delayMs, retryAfterSeconds * 1000);
        }
      }

      await sleep(delayMs);
    }
  }

  throw lastError;
}

/**
 * Silent HTTP request client - no error notifications shown.
 * Use for background operations like search, prefetch, polling.
 */
export const silentRequest = silentRequestWithRetry;

export default request;

// Export useRequest for compatibility (currently a no-op, use @tanstack/react-query directly)
export { useRequest } from '../hooks/useRequest';
